/**
 * useToast Hook - SPEC-019 Task 3.9
 *
 * Manages toast notification queue with auto-dismiss support.
 *
 * @module features/ai-button/hooks/useToast
 * @see SPEC-019 Requirements 4.6, 4.7, 10.4
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  /** Unique toast ID */
  id: string;
  /** Toast type */
  type: ToastType;
  /** Message content */
  message: string;
  /** Whether to show undo button */
  showUndo?: boolean;
  /** Undo callback */
  onUndo?: () => void;
  /** Creation timestamp */
  createdAt: number;
  /** Auto-close delay in ms (0 = no auto-close) */
  autoCloseDelay: number;
}

export interface ShowToastOptions {
  /** Toast type */
  type: ToastType;
  /** Message content */
  message: string;
  /** Whether to show undo button */
  showUndo?: boolean;
  /** Undo callback */
  onUndo?: () => void;
  /** Auto-close delay in ms (default: 5000, 0 = no auto-close) */
  autoCloseDelay?: number;
}

export interface UseToastReturn {
  /** Current toast queue */
  toasts: Toast[];
  /** Show a new toast */
  showToast: (options: ShowToastOptions) => string;
  /** Dismiss a toast by ID */
  dismissToast: (id: string) => void;
  /** Clear all toasts */
  clearAll: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_AUTO_CLOSE_DELAY = 5000;

// ============================================================================
// Utilities
// ============================================================================

let toastIdCounter = 0;

function generateToastId(): string {
  return `toast_${++toastIdCounter}_${Date.now()}`;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useToast Hook
 *
 * Manages a queue of toast notifications with auto-dismiss support.
 * Does not steal focus from current element (Property 17).
 *
 * @returns Toast state and actions
 *
 * @example
 * ```tsx
 * const { toasts, showToast, dismissToast } = useToast();
 *
 * const handleSuccess = () => {
 *   showToast({
 *     type: 'success',
 *     message: 'Changes saved!',
 *     showUndo: true,
 *     onUndo: () => undoChanges(),
 *   });
 * };
 * ```
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const dismissToast = useCallback((id: string) => {
    // Clear timer if exists
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (options: ShowToastOptions): string => {
      const id = generateToastId();
      const autoCloseDelay = options.autoCloseDelay ?? DEFAULT_AUTO_CLOSE_DELAY;

      const toast: Toast = {
        id,
        type: options.type,
        message: options.message,
        showUndo: options.showUndo,
        onUndo: options.onUndo,
        createdAt: Date.now(),
        autoCloseDelay,
      };

      setToasts((prev) => [...prev, toast]);

      // Set auto-dismiss timer if delay > 0
      if (autoCloseDelay > 0) {
        const timer = setTimeout(() => {
          dismissToast(id);
        }, autoCloseDelay);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismissToast]
  );

  const clearAll = useCallback(() => {
    // Clear all timers
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();

    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    dismissToast,
    clearAll,
  };
}
